// Hero.jsx
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, Factory, Wrench, Award, CheckCircle, ClipboardCheck } from 'lucide-react';
import PAUT from "../assets/PAUT1.jpg";
import UT from "../assets/UT.png";
import TOFD from "../assets/Inhouse8.png";


import MTPT from "../assets/MPT1.jpg";
import LPT from "../assets/LPT14.jpg";


const slides = [
    {
        img: PAUT,
        topLabel: "PAUT",
        bottomLabel: "Weld & Joint Integrity",
    },
    {
        img: TOFD,
        topLabel: "TOFD",
        bottomLabel: "Weld & Joint Integrity",
    },
    {
        img: UT,
        topLabel: "Ultrasonic Testing (UT)",
        bottomLabel: "Thickness & Flaw Detection",
    },
    {
        img: MTPT,
        topLabel: "Magnetic Particle Testing (MPT)",
        bottomLabel: "Surface Defect Inspection",
    },
    {
        img: LPT,
        topLabel: "Liquid Penetrant Testing (LPT)",
        bottomLabel: "Surface Defect Inspection",
    },
];

export default function Hero() {
    const [current, setCurrent] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrent((prev) => (prev + 1) % slides.length);
        }, 4000); // change slide every 4 seconds
        return () => clearInterval(interval);
    }, []);
    return (
        <section
            id="home"
            className="relative overflow-hidden bg-white text-gray-900"
            style={{ scrollMarginTop: "88px" }} // <-- offset for sticky navbar
        >
            {/* Industrial background */}
            <div aria-hidden className="pointer-events-none absolute inset-0">
                <svg className="absolute inset-0 w-full h-full opacity-20" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                        <pattern id="grid" width="32" height="32" patternUnits="userSpaceOnUse">
                            <path d="M 32 0 L 0 0 0 32" fill="none" stroke="#93c5fd" strokeWidth="0.5" />
                        </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#grid)" />
                </svg>
                <div className="absolute -top-20 -left-24 h-72 w-72 rounded-full bg-orange-200/40 blur-3xl" />
                <div className="absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-blue-200/40 blur-3xl" />
                <div className="absolute top-10 right-10 h-1 w-40 bg-gradient-to-r from-blue-600 to-orange-500 rounded-full" />
                <div className="absolute bottom-10 left-10 h-1 w-28 bg-gradient-to-r from-orange-500 to-blue-600 rounded-full" />
            </div>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                    {/* Copy */}
                    <motion.div
                        initial={{ opacity: 0, x: -24 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="lg:col-span-6"
                    >
                        <h1 className="text-4xl sm:text-5xl lg:text-5xl font-extrabold text-blue-800 leading-snug">
                            Ensuring Integritys <br />
                            <span className="text-orange-600">Through Advanced NDT</span>
                        </h1>

                        <h2 className="mt-4 text-lg sm:text-xl text-blue-600 max-w-lg">
                            Specialist in Advanced NDT Solutions, Certification & Training Programs.
                        </h2>



                        {/* Trust badges */}
                        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                            <Badge icon={<ShieldCheck size={18} className="text-blue-600" />} text="ISO-Compliant Procedures" />
                            <Badge icon={<Factory size={18} className="text-orange-500" />} text="On-site & In-house" />
                            <Badge icon={<Wrench size={18} className="text-green-500" />} text="Calibrated Equipment" />
                            <Badge icon={<Award size={18} className="text-red-500" />} text="Certified & Accredited Staff" />
                            <Badge icon={<CheckCircle size={18} className="text-purple-500" />} text="Safety & Quality Standards" />
                            <Badge icon={<ClipboardCheck size={18} className="text-teal-500" />} text="Regulatory Compliance" />
                        </div>


                        {/* CTAs */}
                        <div className="mt-8 flex flex-wrap gap-4">
                            <a
                                href="/services"
                                className="px-6 py-3 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-500 text-white font-semibold shadow-lg hover:from-blue-700 hover:to-indigo-600 transition-all"
                            >
                                Explore Solutions
                            </a>
                            <a
                                href="#quote"
                                className="px-6 py-3 rounded-2xl border-2 border-blue-600 text-blue-600 font-semibold hover:bg-blue-600 hover:text-white transition-all"
                            >
                                Get a Free Quote
                            </a>
                        </div>



                    </motion.div>

                    {/* Visual */}
                    <motion.div
                        initial={{ opacity: 0, x: 24 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.1 }}
                        className="lg:col-span-6"
                    >
                        <div className="relative">
                            <div className="absolute -inset-2 rounded-2xl bg-gradient-to-br from-blue-600/20 via-blue-600/0 to-orange-500/20 blur-md" />

                            <div className="relative rounded-2xl border border-gray-200 bg-white shadow-xl overflow-hidden h-[320px] sm:h-[380px] lg:h-[440px]">
                                <AnimatePresence>
                                    <motion.img
                                        key={current}
                                        src={slides[current].img}
                                        alt={slides[current].bottomLabel}
                                        className="w-full h-full object-cover"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.8 }}
                                                    loading="lazy"

                                    />

                                    {/* Top Label */}
                                    <motion.div
                                        key={slides[current].topLabel}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                        transition={{ duration: 0.5 }}
                                        className="absolute top-4 left-4 px-3 py-1 rounded-lg bg-white/90 border border-blue-200 text-xs font-semibold text-blue-800 shadow"
                                    >
                                        {slides[current].topLabel}
                                    </motion.div>

                                    {/* Bottom Label */}
                                    <motion.div
                                        key={slides[current].bottomLabel}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                        transition={{ duration: 0.5 }}
                                        className="absolute bottom-4 right-4 px-3 py-1 rounded-lg bg-white/90 border border-orange-200 text-xs font-semibold text-orange-700 shadow"
                                    >
                                        {slides[current].bottomLabel}
                                    </motion.div>
                                </AnimatePresence>
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Service highlights bar */}
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.2 }}
                    className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-4"
                >
                    <Highlight
                        title="PAUT & TOFD"
                        desc="Advanced ultrasonic inspections using Phased Array Ultrasonic Testing (PAUT) and Time of Flight Diffraction (TOFD) for welds, pipelines, and structural components."
                    />
                    <Highlight
                        title="Weld & Fabrication NDT"
                        desc="Quality control for piping, pressure vessels, and structural welds."
                        variant="orange"
                    />
                    <Highlight
                        title="Advanced NDT Programs"
                        desc="Procedure drafting, Level II/III certifications, audits, and professional training."
                    />
                </motion.div>

            </div>
        </section>
    );
}

/* ===== Small Presentational Components ===== */
function Badge({ icon, text }) {
    return (
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gray-200 bg-white text-xs font-medium">
            <span className="opacity-80">{icon}</span>
            <span>{text}</span>
        </div>
    );
}

function Metric({ value, label, accent }) {
    const color = accent === "orange" ? "text-orange-600" : "text-blue-700";
    return (
        <div className="text-center">
            <div className={`text-2xl sm:text-3xl font-extrabold ${color}`}>{value}</div>
            <div className="text-xs sm:text-sm text-gray-600">{label}</div>
        </div>
    );
}

function Highlight({ title, desc, variant }) {
    const styles =
        variant === "orange"
            ? "border-orange-200/80 hover:border-orange-300/90"
            : "border-blue-200/80 hover:border-blue-300/90";
    return (
        <div className={`group rounded-2xl border bg-white/90 backdrop-blur p-5 shadow-sm transition ${styles}`}>
            <p className="text-base font-semibold text-gray-900">{title}</p>
            <p className="mt-1 text-sm text-gray-600">{desc}</p>
            <div className="mt-3 h-1 w-16 rounded-full bg-gradient-to-r from-blue-600 to-orange-500 group-hover:w-24 transition-all" />
        </div>
    );
}
